import React, { memo, useState, useCallback, useRef } from 'react'
import { useClickAway, useLocalStorage } from 'react-use'
import { FooterLogo, HeaderLogo } from 'components/atoms/logos'
import { Button } from 'transfix-ui/components/Button'
import { INavRoute } from 'types'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useNavContext } from 'utils/context/NavContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { SystemModal } from 'components/SystemModal'
import {
  HelpIcon,
  LogOutIcon,
  OpenInNewTabIcon,
  InfoIcon,
  MenuIcon,
  CollapseIcon,
} from 'transfix-icons'
import { isUserType } from 'utils/userTypes'
import { IS_NAV_BAR_OPEN } from 'constants/nav'
import { SideNavThemes } from './SideNavWrapper'

interface ISideNavProps {
  currentPageTitle?: string
  primaryNavLinks: INavRoute[]
  secondaryNavLinks?: INavRoute[]
  homeRoute?: string
  theme: SideNavThemes
  logo?: React.ReactElement
  onLogout?: () => void
  onSupport?: () => void
  persistNavBar?: boolean
}

export const SideNav = memo<ISideNavProps>(
  ({
    theme,
    currentPageTitle,
    primaryNavLinks,
    secondaryNavLinks = [],
    homeRoute = '#',
    logo,
    onLogout,
    onSupport,
    persistNavBar = false,
  }) => {
    const { logout, userDetails } = useUserDetails()
    const [open, setOpen] = useState(false)
    const toggleFlagsModalHandler = () => setOpen(!open)
    const footerText = `© ${new Date().getFullYear()} Transfix, Inc.`
    const logOutClickHandler = () => (onLogout ? onLogout() : logout())
    const { userType = '' } = userDetails || {}
    const isAdmin = isUserType('admin', userType)
    const { isNavOpen, setNavOpen, toggleNav } = useNavContext()
    const [, setLocalNavBarOpen] = useLocalStorage<boolean>(IS_NAV_BAR_OPEN, true)

    const { isMobile } = useBreakpointContext()
    const toggleNavHandler = () => {
      setLocalNavBarOpen(isNavOpen)
      toggleNav()
    }

    const linkClickHandler = useCallback(() => {
      if (!persistNavBar || isMobile) setNavOpen(false)
    }, [setNavOpen, isMobile, persistNavBar])

    const navRef = useRef(null)
    useClickAway(navRef, () => {
      if ((isNavOpen && !persistNavBar) || isMobile) setNavOpen(false)
    })

    return (
      <>
        <nav
          ref={navRef}
          className={`side-nav flex flex-column justify-between ${isNavOpen ? 'expanded' : ''}`}>
          {/* Primary Nav List */}
          <ul className='side-nav__list'>
            <li>
              <span className='side-nav__item no-hover pt3'>
                <span className={`side-nav__label ${isNavOpen ? 'logo' : 'logo-collapsed'}`}>
                  <a href={homeRoute} className='flex' aria-label='side navigation logo'>
                    {isNavOpen ? (
                      logo || <HeaderLogo logoStyle={theme === 'dark' ? 'white' : 'black'} />
                    ) : (
                      <FooterLogo className='logo-collapsed' logoStyle='white' />
                    )}
                  </a>
                </span>
              </span>
              <div
                className={`items-center justify-between side-nav__title body-large body-semibold pt2 pl3 ${
                  !isNavOpen && 'pb5'
                }`}
              />
            </li>

            {/* Close Menu */}
            <li className={`pb4 ${isNavOpen ? 'pt3' : 'pt1'}`}>
              <Button
                data-test='close-menu'
                color='tertiary'
                onClick={toggleNavHandler}
                className='pa0 flex items-center side-nav__item'>
                <span className='side-nav__icon flex items-center'>
                  {isNavOpen ? <CollapseIcon /> : <MenuIcon />}
                  <div className='tooltip' aria-hidden='true'>
                    Open Menu
                  </div>
                </span>
                <span className='side-nav__label'>Close Menu</span>
              </Button>
            </li>

            {/* Primary Navigation Links */}
            {primaryNavLinks.map(({ IconEl, comingSoon, location, name }) => (
              <li key={`navlink-${name}`}>
                {comingSoon && (
                  <div className='flex items-center side-nav__item' data-test={name}>
                    <span className='side-nav__icon flex items-center'>
                      {IconEl && <IconEl />}
                      <div className='tooltip' aria-hidden='true'>
                        {name} (coming soon)
                      </div>
                    </span>
                    <span className='side-nav__label'>
                      {name}&nbsp;
                      <span className='tag tag-badge tag-disabled'>Coming Soon</span>
                    </span>
                  </div>
                )}
                {!comingSoon && (
                  <a
                    {...(name === currentPageTitle && { 'aria-current': 'page' })}
                    className={`flex items-center side-nav__item ${
                      name === currentPageTitle ? 'active' : ''
                    }`}
                    href={location}
                    id={name}
                    data-test={name}
                    onClick={linkClickHandler}>
                    <span className='side-nav__icon flex items-center'>
                      {IconEl && <IconEl />}
                      <div className='tooltip' aria-hidden='true'>
                        {name}
                      </div>
                    </span>
                    <span className='side-nav__label'>{name}</span>
                  </a>
                )}
              </li>
            ))}
          </ul>

          {/* Secondary Nav List */}
          <ul className='side-nav__list'>
            {onSupport && (
              <li className='pb0'>
                <Button
                  data-test='Help'
                  onClick={onSupport}
                  color='tertiary'
                  className='pa0 flex items-center side-nav__item'>
                  <span className='side-nav__icon flex items-center'>
                    <HelpIcon />
                    <div className='tooltip' aria-hidden='true'>
                      Support
                    </div>
                  </span>
                  <span className='side-nav__label'>Support</span>
                </Button>
              </li>
            )}

            {/* Secondary Nav Links */}
            {secondaryNavLinks.map(({ IconEl, isExternal, location, name }) => (
              <li key={`navlink-${name}`} className='pb0'>
                <a
                  {...(name === currentPageTitle && { 'aria-current': 'page' })}
                  className={`flex items-center side-nav__item ${
                    name === currentPageTitle ? 'active' : ''
                  }`}
                  href={location}
                  target={isExternal ? '_blank' : '_self'}
                  rel='noopener noreferrer'
                  id={name}
                  onClick={linkClickHandler}
                  data-test={name}>
                  <span className='side-nav__icon flex items-center'>
                    {IconEl && <IconEl />}
                    <div className='tooltip' aria-hidden='true'>
                      {name}
                    </div>
                  </span>
                  <span className='side-nav__label flex items-center'>
                    {name}
                    {isExternal && (
                      <>
                        &nbsp;
                        <OpenInNewTabIcon className='open-new-tab' />
                      </>
                    )}
                  </span>
                </a>
              </li>
            ))}

            {/* Log Out */}
            <li className='pb0'>
              <Button
                data-test='Log Out'
                onClick={logOutClickHandler}
                color='tertiary'
                className='pa0 flex items-center side-nav__item'>
                <span className='side-nav__icon flex items-center'>
                  <LogOutIcon />
                  <div className='tooltip' aria-hidden='true'>
                    Log Out
                  </div>
                </span>
                <span className='side-nav__label'>Log Out</span>
              </Button>
            </li>
            {isAdmin && (
              <li className='pb0'>
                <Button
                  onClick={toggleFlagsModalHandler}
                  color='tertiary'
                  className='pa0 flex items-center side-nav__item'>
                  <span className='side-nav__icon flex items-center'>
                    <InfoIcon />
                    <div className='tooltip' aria-hidden='true'>
                      {footerText}
                    </div>
                  </span>
                  <span className='side-nav__label'>{footerText}</span>
                </Button>
              </li>
            )}
          </ul>
        </nav>
        {open && <SystemModal open={open} close={toggleFlagsModalHandler} />}
      </>
    )
  }
)
